import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { FlaskConical, ArrowRight } from "lucide-react";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { CONCEPT_PROJECTS } from "@/config/homeContent";
import { api } from "@/lib/api";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ConceptShowcase = () => {
  // Concept projects are managed in the CMS (Admin → Portfolio); static config is the fallback.
  const [items, setItems] = useState(CONCEPT_PROJECTS);

  useEffect(() => {
    api
      .get("/portfolio?kind=concept")
      .then((r) => {
        if (r.data?.length) {
          setItems(r.data.map((p) => ({ name: p.title, kind: p.tags?.[0] || p.label, label: p.label, summary: p.summary, tags: p.tags || [], image: p.image })));
        }
      })
      .catch(() => {});
  }, []);

  return (<Section testid="concept-showcase-section" className="bg-brand-charcoal/40">
    <Container className="space-y-12">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading
          eyebrow="Concept showcase"
          title="Proof of craft, honestly labelled"
          description="These are in-house concept projects and demonstrations — not client engagements. They exist to show how we think, design and build."
          testid="concept-heading"
        />
        <p
          data-testid="concept-disclaimer"
          className="inline-flex w-fit items-center gap-2 rounded-full border border-brand-azure/30 bg-brand-azure/10 px-4 py-2 text-xs font-medium text-brand-azure"
        >
          <FlaskConical size={13} /> Every project below is a concept or demonstration
        </p>
        <Link
          to="/contact?source=concept-showcase"
          data-testid="concept-enquiry-link"
          className="group inline-flex w-fit items-center gap-2 text-sm font-medium text-brand-emerald"
        >
          Want something like this? Start a conversation
          <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="concept-grid">
        {items.map((p, i) => (
          <motion.article
            key={p.name}
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            custom={i}
            data-testid={`concept-card-${p.name.toLowerCase().replace(/\s/g, "-")}`}
            className="group overflow-hidden rounded-xl border border-brand-mist/12 bg-brand-charcoal transition-colors duration-300 hover:border-brand-emerald/30"
          >
            <div className="relative h-52 overflow-hidden sm:h-60">
              <img
                src={p.image}
                alt={`${p.name} — ${p.kind} visual`}
                loading="lazy"
                decoding="async"
                width="800"
                height="450"
                className="h-full w-full object-cover opacity-80 transition-transform duration-700 ease-brand group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/20 to-transparent" />
              <span className="absolute left-4 top-4 rounded-full border border-brand-azure/40 bg-brand-black/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-azure backdrop-blur-sm">
                {p.label}
              </span>
            </div>
            <div className="space-y-3 p-6">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-brand-mist">{p.kind}</p>
              <h3 className="font-display text-xl font-semibold text-brand-snow">{p.name}</h3>
              <p className="text-sm leading-relaxed text-brand-mist">{p.summary}</p>
              <ul className="flex flex-wrap gap-2 pt-1">
                {p.tags.map((t) => (
                  <li key={t} className="rounded-full border border-brand-mist/15 px-3 py-1 text-[11px] text-brand-mist">
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        ))}
      </div>
    </Container>
  </Section>
  );
};

export default ConceptShowcase;
